/*
 * Edit Job Comment Form
 */

import * as React from 'react';

import { AbstractControl, FieldConfig, FormGenerator, FormGroup, GroupProps } from 'react-reactive-form';
import { IjobComment, IdeficiencyCode, Iuser, Ioption } from '../../models';
import {
    addJobComment,
    deleteJobComment,
    setSelectedJobComment,
    toggleEditJobCommentModal,
    updateJobComment
} from '../../actions/manageJobCommentsActions';

import { Button, Col } from 'react-bootstrap';
import { FormUtil } from '../common/FormUtil';
import { RouteComponentProps } from 'react-router';
import { constants } from '../../constants/constants';
import { initialJobComment } from '../../reducers/initialState';
import { toastr } from 'react-redux-toastr';
import { TFunction } from 'react-i18next';
import InlinePhotoControl, { InlinePhotoControlHandle } from '../photos/InlinePhotoControl';
import { v4 as uuidv4 } from 'uuid';

interface Iprops {
    selectedJobComment: IjobComment;
    loading: boolean;
    colorButton: string;
    t: TFunction;
    toggleEditJobCommentModal: typeof toggleEditJobCommentModal;
    user: Iuser;
    addJobComment: typeof addJobComment;
    updateJobComment: typeof updateJobComment;
    setSelectedJobComment: typeof setSelectedJobComment;
    deleteJobComment: typeof deleteJobComment;
    deficiencyCodesByID: { [key: string]: IdeficiencyCode };
    jobID: string;
    installBaseID: string;
    isJobReadOnly: boolean;
}
interface Istate {
    fieldConfig: FieldConfig;
    preGeneratedCommentID: string;
}

export class EditJobCommentForm extends React.Component<Iprops & RouteComponentProps<Record<string, never>>, Istate> {
    private formGroup: FormGroup | undefined;
    private subscription: any;
    private photoRef = React.createRef<InlinePhotoControlHandle>();

    constructor(props: Iprops & RouteComponentProps<Record<string, never>>) {
        super(props);
        this.state = {
            fieldConfig: FormUtil.translateForm(this.buildFieldConfig(this.props.selectedJobComment), this.props.t),
            preGeneratedCommentID: props.selectedJobComment.id || uuidv4()
        };
    }

    componentDidMount() {
        if (!this.props.selectedJobComment) {
            console.error('[componentDidMount]: missing job');
            return;
        }
    }
    componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    subscribeToValueChanges = () => {
        if (this.formGroup) {
            const codeForm = this.formGroup.get('codeID');
            this.subscription = codeForm.valueChanges.subscribe((code: Ioption) => {
                const { text } = this.formGroup?.value || {};

                this.setState({
                    fieldConfig: FormUtil.translateForm(
                        this.buildFieldConfig({
                            ...this.formGroup?.value,
                            text: `${text}${this.props.deficiencyCodesByID[code.value]?.description || ''}`
                        }),
                        this.props.t
                    )
                });
            });
        }
    };

    buildFieldConfig = ({ text, isDeficiency, isInternal, codeID }: IjobComment = initialJobComment) => {
        const disabled = this.props.isJobReadOnly;
        const { deficiencyCodesByID } = this.props;

        const selectedCode = codeID ? FormUtil.convertToSingleOption(deficiencyCodesByID[codeID]) : null;

        let controls = {
            codeID: {
                render: FormUtil.Select,
                meta: {
                    label: 'Standard Text',
                    colWidth: 12,
                    isMulti: false,
                    name: 'code',
                    required: false,
                    options: FormUtil.convertToOptions(this.props.deficiencyCodesByID)
                },
                formState: { value: selectedCode, disabled }
            },
            text: {
                render: FormUtil.TinyMCERichTextEditor,
                meta: {
                    showNormalToolbar: false,
                    label: 'comment',
                    colWidth: 12,
                    componentClass: 'textarea',
                    type: 'input',
                    name: 'comment',
                    required: true,
                    rows: 10,
                    initialContent: text,
                    charLimit: 1000,
                    overrideRTEText: true
                },
                options: {
                    validators: FormUtil.validators.requiredWithTrim
                },
                formState: { value: text, disabled }
            },
            isDeficiency: {
                render: FormUtil.Toggle,
                meta: { label: 'labelIsDeficiency', colWidth: 12 },
                formState: { value: isDeficiency, disabled }
            },
            isInternal: {
                render: FormUtil.Toggle,
                meta: { label: 'labelIsInternal', colWidth: 12 },
                formState: { value: isInternal, disabled }
            }
        } as { [key: string]: GroupProps };

        return {
            controls
        } as FieldConfig;
    };

    handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (this.formGroup === undefined || this.props.isJobReadOnly) {
            return;
        }
        if (this.formGroup.status === 'INVALID') {
            this.formGroup.markAsSubmitted();
            toastr.error('Please check invalid inputs', '', constants.toastrError);
            return;
        }

        if (this.props.selectedJobComment.id.length) {
            this.props.updateJobComment(this.formGroup.value);
        } else {
            this.props.addJobComment(this.formGroup.value, this.state.preGeneratedCommentID);
        }
        this.photoRef.current?.commitChanges();
        this.props.toggleEditJobCommentModal();
        this.props.setSelectedJobComment(initialJobComment);
    };

    handleDelete = (e: any) => {
        e.preventDefault();
        this.props.deleteJobComment(this.props.selectedJobComment);
        this.props.toggleEditJobCommentModal();
    };

    setForm = (form: AbstractControl) => {
        this.formGroup = form as FormGroup;
        this.formGroup.meta = {
            loading: this.props.loading
        };
        this.subscribeToValueChanges();
    };
    render() {
        const { t } = this.props;
        const { selectedJobComment } = this.props;
        if (!selectedJobComment) {
            return null;
        }
        return (
            <div className="job-details-content beacon-form">
                <form onSubmit={this.handleSubmit} className="clearfix beacon-form">
                    <FormGenerator onMount={this.setForm} fieldConfig={this.state.fieldConfig} />
                    <Col xs={12}>
                        <InlinePhotoControl
                            ref={this.photoRef}
                            jobID={this.props.jobID}
                            installBaseID={this.props.installBaseID}
                            jobCommentID={this.state.preGeneratedCommentID}
                            readOnly={this.props.isJobReadOnly}
                            t={t}
                        />
                    </Col>
                    <Col xs={12} className="form-buttons">
                        <Button
                            variant="default"
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                                this.photoRef.current?.resetChanges();
                                this.props.toggleEditJobCommentModal();
                            }}
                        >
                            {String(t(this.props.isJobReadOnly ? 'common:close' : 'cancel'))}
                        </Button>
                        {this.props.isJobReadOnly === false && this.props.selectedJobComment.id.length > 0 && (
                            <Button
                                disabled={this.props.loading}
                                variant="default"
                                type="button"
                                className="margin-right-1"
                                onClick={this.handleDelete}
                            >
                                {String(t('delete'))}
                            </Button>
                        )}
                        {this.props.isJobReadOnly === false && (
                            <Button variant={this.props.colorButton} type="submit" disabled={this.props.loading}>
                                {String(t('save'))}
                            </Button>
                        )}
                    </Col>
                </form>
            </div>
        );
    }
}
